import type { ProductAd } from '@/scopes/product-ads/domain/types'

import { horizontalGap, tk } from '@sunwebgroup/ui-components'
import useEmblaCarousel from 'embla-carousel-react'
import { useCallback, useEffect, useState } from 'react'
import { styled } from 'styled-components'
import { ProductAdLegacy as ProductAdComponent } from './ProductAdLegacy'
import { ProductAdLoadingLegacy } from './ProductAdLoadingLegacy'

const Slider = styled.div`
  overflow: hidden;
  width: calc(100% + ${tk('spacing_xl')});
  margin-left: -${tk('spacing_md')};
  margin-right: -${tk('spacing_md')};

  @media ${tk('mq_desktop_sm')} {
    width: inherit;
    margin-left: -${tk('spacing_3xl')};
    margin-right: -${tk('spacing_3xl')};
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  padding: ${tk('spacing_2xs')}; // needed to show the shadow of the items
  @media ${tk('mq_desktop_lg')} {
    width: 800px;
  }
  @media ${tk('mq_desktop_xl')} {
    width: 940px;
  }
  ${horizontalGap('spacing_md')}
`

const ProductAdWrapper = styled.div`
  &:first-child {
    margin-left: ${tk('spacing_sm')};
  }

  &:last-child {
    margin-right: ${tk('spacing_sm')};
  }

  @media ${tk('mq_desktop_sm')} {
    &:first-child {
      margin-left: ${tk('spacing_2xl')};
    }

    &:last-child {
      margin-right: ${tk('spacing_2xl')};
    }
  }
`

interface ProductAdsListProps {
  data: ProductAd[]
  isLoading?: boolean
  type: string
}
/**
 * @deprecated This file is deprecated and will be removed in the future.
 * TODO: To be deleted when FF ACTIVATE_UDS_SEARCH_PRODUCT_CARD is deleted.
 */
export function ProductAdsListLegacy({ data, isLoading, type }: ProductAdsListProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    dragFree: true,
  })
  const [slidesInView, setSlidesInView] = useState<number[]>([])

  const findSlidesInView = useCallback(() => {
    if (!emblaApi)
      return

    setSlidesInView((slidesInViewElements) => {
      if (slidesInViewElements.length === emblaApi.slideNodes().length) {
        emblaApi.off('slidesInView', findSlidesInView)
      }
      const inView = emblaApi.slidesInView().filter(index => !slidesInViewElements.includes(index))
      return slidesInViewElements.concat(inView)
    })
  }, [emblaApi, setSlidesInView])

  useEffect(() => {
    if (!emblaApi)
      return
    emblaApi.on('slidesInView', findSlidesInView)
    emblaApi.on('reInit', findSlidesInView)
  }, [emblaApi, findSlidesInView])

  const adsLoadings = []
  for (let i = 0; i < 5; i++) {
    adsLoadings.push(
      <ProductAdWrapper>
        <ProductAdLoadingLegacy />
      </ProductAdWrapper>,
    )
  }

  if (isLoading) {
    return (
      <Slider>
        <Wrapper>{adsLoadings}</Wrapper>
      </Slider>
    )
  }

  return (
    <Slider ref={emblaRef}>
      <Wrapper>
        {data.map((productAd, index) => (
          <ProductAdWrapper key={productAd.adId}>
            <ProductAdComponent data={productAd} inView={slidesInView.includes(index)} componentType={type} />
          </ProductAdWrapper>
        ))}
      </Wrapper>
    </Slider>
  )
}
